import { useCallback, useEffect, useRef, useState } from 'react';

import type { ResourceCache } from './state/asyncListCache';

// Single-resource SWR hook — the Logger's own copy of
// apps/mobile/src/hooks/useAsyncResource.ts (RW-18), ported verbatim
// except the error is `unknown` instead of mobile's ApiError class (this
// package has no app-local error type to import, and none of this
// package's callers inspect the error's shape beyond truthiness). A
// genuinely generic, side-effect-free hook — duplicating it is the same
// pragmatic call as asyncListCache.ts (see that file's header): it's
// mobile-app-wide (progress, catalog, programs), so it stays owned there
// too rather than moving.
//
// Semantics:
//   - Mount: read cache, render if present, fire fetch in background.
//   - Refresh: re-fetch, replace state, persist cache.
//   - Abort: every refresh allocates a fresh AbortController; a
//     re-entry cancels the in-flight call (most-recent wins).

type AsyncResourceStatus =
  | 'idle'
  | 'loading'
  | 'loaded'
  | 'refreshing'
  | 'error';

type Options<T> = {
  key: string;
  fetch: (signal?: AbortSignal) => Promise<T>;
  cache?: ResourceCache<T>;
  immediate?: boolean;
};

type AsyncResourceState<T> = {
  value: T | null;
  status: AsyncResourceStatus;
  error: unknown;
  refresh: () => Promise<void>;
};

export function useAsyncResource<T>(opts: Options<T>): AsyncResourceState<T> {
  const { key, fetch, cache, immediate = true } = opts;
  const [value, setValue] = useState<T | null>(null);
  const [status, setStatus] = useState<AsyncResourceStatus>('idle');
  const [error, setError] = useState<unknown>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fetchRef = useRef(fetch);
  const cacheRef = useRef(cache);
  useEffect(() => {
    fetchRef.current = fetch;
    cacheRef.current = cache;
  }, [fetch, cache]);

  const refresh = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStatus((prev) => (prev === 'loaded' ? 'refreshing' : 'loading'));
    setError(null);
    try {
      const fresh = await fetchRef.current(ctrl.signal);
      if (ctrl.signal.aborted) return;
      setValue(fresh);
      setStatus('loaded');
      if (cacheRef.current) {
        void cacheRef.current.write(fresh);
      }
    } catch (e) {
      if (ctrl.signal.aborted) return;
      setStatus('error');
      setError(e);
    }
  }, []);

  useEffect(() => {
    if (!immediate) return;
    let cancelled = false;
    void (async () => {
      if (cacheRef.current) {
        const cached = await cacheRef.current.read();
        if (cancelled) return;
        if (cached !== null) {
          setValue(cached);
          setStatus('loaded');
        }
      }
      if (cancelled) return;
      void refresh();
    })();
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [key, immediate, refresh]);

  return { value, status, error, refresh };
}
